import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiBox, FiChevronLeft, FiChevronRight, FiDownload, FiEye, FiFilePlus, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import './AdminUI.css';
import { AdminProductExport } from './AdminProductExport';

export function AdminProducts() {
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalProducts] = useState(0);
  const [activeTab, setActiveTab] = useState('products');
  const navigate = useNavigate();
  const limit = 30;

  const fetchProducts = async (page, query = "") => {
    setLoading(true);
    setError(null);
    try {
      const url = query
        ? `https://valleyapi-dr9y.onrender.com/api/products/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
        : `https://valleyapi-dr9y.onrender.com/api/products?page=${page}&limit=${limit}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      const list = Array.isArray(data.products) ? data.products : [];
      const visibleProducts = query ? list : list.filter((product) => product.published === 'Yes');
      setFilteredProducts(visibleProducts);
      setTotalPages(data.totalPages || 1);
      setTotalProducts(data.totalProducts || 0);
    } catch (fetchError) {
      console.error("Error loading products:", fetchError);
      setError(query ? "Products could not be searched. Please try again." : "Products could not be loaded. Please try again.");
      setFilteredProducts([]);
      setTotalPages(1);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token || token === 'null') {
      alert("You are not authenticated. Please log in.");
      navigate('/admin-login');
      return false;
    }
    return true;
  };

  const handleUnauthorized = React.useCallback(() => {
    localStorage.removeItem('admin_token');
    alert("Session expired. Please log in again.");
    navigate('/admin-login');
  }, [navigate]);

  const handleDelete = async (productName) => {
    if (!checkAuth()) return;
    if (!window.confirm(`Delete “${productName}”? This action cannot be undone.`)) return;
    setLoading(true);
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/products/${encodeURIComponent(productName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await response.json();
      if (response.ok) {
        setFilteredProducts((current) => current.filter((product) => product.product_name !== productName));
        setTotalProducts((current) => Math.max(0, current - 1));
      } else if (response.status === 401) {
        alert("Session expired. Please log in again.");
        navigate('/admin-login');
      } else {
        alert(`Failed to delete product: ${data.error}`);
      }
    } catch (deleteError) {
      console.error("Error deleting product:", deleteError);
      alert("Error deleting product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(inputValue.trim());
    setCurrentPage(1);
  };

  const visiblePages = () => {
    const pages = [];
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    const end = Math.min(totalPages, Math.max(5, currentPage + 2));
    for (let page = start; page <= end; page += 1) pages.push(page);
    return pages;
  };

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Catalog management</span>
          <h1 className="va-title">Products</h1>
          <p className="va-subtitle">Maintain product information, availability, pricing and publication status.</p>
        </div>
        <div className="va-header-actions">
          <button className="va-button va-button--secondary" type="button" onClick={() => setActiveTab('export')}><FiDownload /> Export CSV</button>
          <Link to="/admin/products/addcsv" className="va-button va-button--secondary"><FiFilePlus /> Import CSV</Link>
          <Link to="/admin/products/add" className="va-button"><FiPlus /> Add product</Link>
        </div>
      </header>

      <div className="va-tabs" role="tablist" aria-label="Product administration views">
        <button id="products-list-tab" className={`va-tab ${activeTab === 'products' ? 'is-active' : ''}`} type="button" role="tab" aria-selected={activeTab === 'products'} aria-controls="products-list-panel" onClick={() => setActiveTab('products')}>Product list</button>
        <button id="products-export-tab" className={`va-tab ${activeTab === 'export' ? 'is-active' : ''}`} type="button" role="tab" aria-selected={activeTab === 'export'} aria-controls="products-export-panel" onClick={() => setActiveTab('export')}>Export CSV</button>
      </div>

      {activeTab === 'export' ? <AdminProductExport onUnauthorized={handleUnauthorized} /> : <div id="products-list-panel" role="tabpanel" aria-labelledby="products-list-tab">

      <div className="va-toolbar">
        <div className="va-toolbar__group">
          <div className="va-search">
            <FiSearch />
            <input type="search" placeholder="Search product name or brand" value={inputValue} onChange={(event) => setInputValue(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && handleSearch()} aria-label="Search products" />
          </div>
          <button className="va-button va-button--secondary" onClick={handleSearch}>Search</button>
        </div>
        <span className="va-results">{filteredProducts.length} shown</span>
      </div>

      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}

      <div className="va-table-wrap" style={{ position: 'relative' }}>
        {loading && <div className="va-loading" style={{ position: 'absolute', inset: 0, zIndex: 2, minHeight: 0, background: 'rgba(255,255,255,.78)' }}><div className="va-spinner" aria-label="Loading products" /></div>}
        <table className="va-table">
          <thead><tr><th>Product</th><th>Brand</th><th>Category</th><th>Status</th><th>Stock</th><th>Price</th><th></th></tr></thead>
          <tbody>
            {filteredProducts.length > 0 ? filteredProducts.map((product, index) => (
              <tr key={product.idproducts || index}>
                <td><span className="va-table__primary">{product.product_name}</span><span className="va-table__secondary" title={product.product_description}>{product.product_description || 'No description'}</span></td>
                <td>{product.product_brand || '—'}</td>
                <td>{product.product_category || '—'}</td>
                <td><span className={`va-badge ${product.published === 'Yes' ? 'va-badge--success' : 'va-badge--warning'}`}>{product.published === 'Yes' ? 'Published' : 'Draft'}</span></td>
                <td className="va-number"><span className={`va-badge ${Number(product.product_stock || 0) < 10 ? 'va-badge--danger' : 'va-badge--success'}`}>{product.product_stock ?? 0}</span></td>
                <td className="va-number"><strong>${Number(product.product_price || 0).toFixed(2)}</strong></td>
                <td><div className="va-table__actions">
                  <button className="va-button va-button--ghost va-button--icon" onClick={() => navigate(`/admin/products/${encodeURIComponent(product.product_name)}`)} title="View product" aria-label={`View ${product.product_name}`}><FiEye /></button>
                  <button className="va-button va-button--ghost va-button--icon" onClick={() => handleDelete(product.product_name)} title="Delete product" aria-label={`Delete ${product.product_name}`}><FiTrash2 /></button>
                </div></td>
              </tr>
            )) : (
              <tr><td colSpan="7"><div className="va-empty"><div><FiBox /><strong>No products found</strong><p>{searchQuery ? `No matches for “${searchQuery}”.` : 'Add your first product to get started.'}</p></div></div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="va-pagination">
          <span className="va-results">Page {currentPage} of {totalPages}</span>
          <div className="va-pagination__buttons">
            <button className="va-page-button" onClick={() => setCurrentPage((page) => page - 1)} disabled={currentPage === 1} aria-label="Previous page"><FiChevronLeft /></button>
            {visiblePages().map((page) => <button key={page} className={`va-page-button ${currentPage === page ? 'is-active' : ''}`} onClick={() => setCurrentPage(page)}>{page}</button>)}
            <button className="va-page-button" onClick={() => setCurrentPage((page) => page + 1)} disabled={currentPage === totalPages} aria-label="Next page"><FiChevronRight /></button>
          </div>
        </div>
      )}
      </div>}
    </div>
  );
}
